import Buildable from "../language/Buildable.js"
import type { SignerAsync } from "../language/SignerAsync.js"
import HTTPMethod from "./HTTPMethod.js"
import HTTPRequest from "./HTTPRequest.js"
import type { HTTPRequestBody } from "./HTTPRequestBody.js"

/**
 * @description
 *	An HTTP/HTTPS request.
 */
export default class HTTPRequestBuilder<I> implements Buildable<HTTPRequest<I>> {
	public _body: HTTPRequestBody
	public _headers: Record<string, string>
	public _method: HTTPMethod
	public _timeoutInMilliseconds: number
	public _uri: URL | undefined

	public constructor() {
		this._body = undefined
		this._headers = {}
		this._method = HTTPMethod.GET
		this._timeoutInMilliseconds = 5 * 1000
		this._uri = undefined
	}

	public body(body: I): HTTPRequestBuilder<I> {
		if (typeof body === "object") {
			this._body = JSON.stringify(body, undefined, 0)
		} else if (typeof body === "string") {
			this._body = body
		} else if (body === undefined) {
			this._body = undefined
		} else {
			throw new Error(`Unsupported request body type: ${typeof body}.`)
		}
		return this
	}

	public build(): HTTPRequest<I> {
		return new HTTPRequest<I>(this)
	}

	public header(key: string, value: string): HTTPRequestBuilder<I> {
		this._headers = {
			...this._headers,
			[key]: value
		}
		return this
	}

	public method(method: HTTPMethod): HTTPRequestBuilder<I> {
		this._method = method
		return this
	}

	public async signAsync(signer: SignerAsync<HTTPRequestBuilder<I>>): Promise<HTTPRequest<I>> {
		await signer(this)
		return this.build()
	}

	public timeout(milliseconds: number): HTTPRequestBuilder<I> {
		if (milliseconds < 0) {
			throw new Error("The timeout cannot be negative.")
		}
		this._timeoutInMilliseconds = milliseconds
		return this
	}

	/**
	 * @todo Enhance segments parsing and joining.
	 */
	public uri(uri: URL, ...segments: string[]): HTTPRequestBuilder<I> {
		this._uri = new URL(segments.join(""), uri)
		return this
	}
}
